import {
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Dialog,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@material-ui/core';
import {
  Delete,
  Edit,
  MoreVert,
  Notifications,
  NotificationsNone,
} from '@material-ui/icons';
import {
  ColorDynamic,
  Column,
  Columns,
  Inline,
  useSnackbarStack,
} from '@superdispatch/ui';
import { Box, Button } from '@superdispatch/ui-lab';
import { ReactNode, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { logInfo } from 'shared/helpers/Logger';
import { updateSearchQuery } from 'shared/utils/URLUtils';
import styled from 'styled-components';
import { trackLoadboardEvent } from '../../core/LoadboardAnalytics';
import {
  SavedSearchExpirationText,
  SavedSearchTitle,
} from '../../core/saved-searches/SavedSearchHelpers';
import {
  AlertDuration,
  alertDurationOptions,
  formatActivatedMessage,
  isSavedSearchMuted,
  PostingSavedSearchAlertDTO,
  PostingSavedSearchDTO,
} from '../../data/dto/PostingSavedSearchDTO';
import { convertPostingPageParamsToQuery } from '../../data/dto/PostingsPageParamsDTO';
import { useLoadboardContext } from '../../data/LoadboardContext';
import {
  useDeleteSavedSearch,
  useEditSavedSearchAlert,
} from '../../data/LoadboardSavedSearchesAPI';

interface ListCardItemProps {
  actions: ReactNode;
  children: ReactNode;
  onClick: () => void;
  ariaLabel: string;
}

function ListCardItem({
  actions,
  children,
  onClick,
  ariaLabel,
}: ListCardItemProps) {
  return (
    <Card role="button" aria-label={ariaLabel} onClick={onClick}>
      <CardContent>
        <Columns align="center">
          <Column>{children}</Column>

          <Column width="content">
            <CardActions>{actions}</CardActions>
          </Column>
        </Columns>
      </CardContent>
    </Card>
  );
}

interface SavedSearchListItemProps {
  savedSearch: PostingSavedSearchDTO;
  refetchSavedSearches: () => void;
}

const Styled6 = styled.div`
  background-color: ${ColorDynamic.White};
  flex: 1;
  padding: 16px;
`;

export function SavedSearchListItem({
  savedSearch,
  refetchSavedSearches,
}: SavedSearchListItemProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const {
    router: { openSavedSearchScreen },
  } = useLoadboardContext();
  const { addSnackbar } = useSnackbarStack();

  const isMuted = isSavedSearchMuted(savedSearch);
  const [activeDialog, setActiveDialog] = useState<'delete' | 'unmute'>();

  const { isPending: isDeleting, mutate: deleteSavedSearch } =
    useDeleteSavedSearch({
      onMutate() {
        logInfo('Delete saved search', { savedSearch });
      },
      onSuccess() {
        setActiveDialog(undefined);
        refetchSavedSearches();

        trackLoadboardEvent({ name: 'SLB: Remove Load Alert', savedSearch });

        addSnackbar('Load Alert has been deleted', { variant: 'success' });
      },
      onError() {
        addSnackbar('Something went wrong, please try again later.', {
          variant: 'error',
        });
      },
    });

  const { isPending: isUpdating, mutate: editSavedSearchAlert } =
    useEditSavedSearchAlert({
      onMutate([updatedSavedSearch, alert]) {
        logInfo('Update saved search alert', {
          alert,
          savedSearch,
          updatedSavedSearch,
        });
      },
      onSuccess(_, [, alert]) {
        trackLoadboardEvent({
          name: 'SLB: Updated Load Alert Criteria',
          savedSearch: { ...savedSearch, ...alert }, // Response does not include updated fields.
          prevSavedSearch: savedSearch,
        });
        if (alert.alert_enabled && alert.alert_duration) {
          addSnackbar(formatActivatedMessage(alert.alert_duration));
        }

        if (!alert.alert_enabled) {
          addSnackbar('Load Alert muted.');
        }

        refetchSavedSearches();
      },
      onError({ message }) {
        addSnackbar(message);
      },
    });

  function updateAlert(alert: PostingSavedSearchAlertDTO) {
    editSavedSearchAlert([savedSearch, alert]);
  }

  return (
    <>
      <ListCardItem
        ariaLabel="Open Load Alert"
        onClick={() => {
          trackLoadboardEvent({ name: 'SLB: Clicked Load Alert', savedSearch });
          openSavedSearchScreen({ savedSearch, source: 'Load Alert' });
        }}
        actions={
          <>
            <IconButton
              size="small"
              aria-label="actions"
              onClick={(event) => {
                event.stopPropagation();
                setAnchor(event.currentTarget);
              }}
            >
              <MoreVert />
            </IconButton>

            <Menu
              open={!!anchor}
              anchorEl={anchor}
              onClose={() => {
                setAnchor(null);
              }}
              onClick={(event) => {
                setAnchor(null);
                event.stopPropagation();
              }}
            >
              <Box paddingRight="xsmall" paddingLeft="xsmall">
                <Inline space="xsmall" noWrap={true}>
                  <IconButton
                    disabled={isUpdating}
                    aria-label={isMuted ? 'unmute alert' : 'mute alert'}
                    onClick={
                      isUpdating
                        ? undefined
                        : () => {
                            trackLoadboardEvent({
                              name: 'SLB: UI Interaction',
                              type: isMuted
                                ? 'Load Alert: Clicked Muted Bell Icon'
                                : 'Load Alert: Clicked Active Bell Icon',
                            });
                            if (isMuted) {
                              setActiveDialog('unmute');
                            } else {
                              updateAlert({ alert_enabled: false });
                            }
                          }
                    }
                  >
                    {isUpdating ? (
                      <CircularProgress size={24} />
                    ) : isMuted ? (
                      <NotificationsNone fontSize="small" />
                    ) : (
                      <Notifications color="primary" fontSize="small" />
                    )}
                  </IconButton>

                  <IconButton
                    onClick={() => {
                      navigate({
                        pathname: '/filter',
                        search: updateSearchQuery(location.search, () =>
                          convertPostingPageParamsToQuery({
                            utm_medium: 'Load Alert List',
                            current_saved_search_guid: savedSearch.guid,
                            search_criteria: savedSearch.search_criteria,
                          }),
                        ),
                      });
                    }}
                  >
                    <Edit color="action" fontSize="small" />
                  </IconButton>

                  <IconButton
                    aria-label="Delete load alert"
                    onClick={() => {
                      setActiveDialog('delete');
                    }}
                  >
                    <Delete color="action" fontSize="small" />
                  </IconButton>
                </Inline>
              </Box>
            </Menu>
          </>
        }
      >
        <Typography noWrap={true}>
          <SavedSearchTitle savedSearch={savedSearch} />
        </Typography>

        {!isMuted && (
          <Typography noWrap={true} color="textSecondary">
            <SavedSearchExpirationText savedSearch={savedSearch} />
          </Typography>
        )}
      </ListCardItem>

      <Dialog
        open={activeDialog === 'delete'}
        onClose={
          isDeleting
            ? undefined
            : () => {
                setActiveDialog(undefined);
              }
        }
        fullWidth={true}
        aria-labelledby="SavedSearchListItemDialogTitle"
      >
        <Styled6>
          <form>
            <Grid container={true} spacing={2}>
              <Grid item={true} xs={12}>
                <Typography id="SavedSearchListItemDialogTitle">
                  Delete this Load Alert?
                </Typography>
              </Grid>

              <Grid item={true} xs={12}>
                <Button
                  variant="critical"
                  fullWidth={true}
                  pending={isDeleting}
                  disabled={isDeleting}
                  onClick={() => {
                    deleteSavedSearch(savedSearch.guid);
                  }}
                >
                  Delete
                </Button>
              </Grid>

              <Grid item={true} xs={12}>
                <Button
                  variant="neutral"
                  fullWidth={true}
                  disabled={isDeleting}
                  onClick={() => {
                    setActiveDialog(undefined);
                  }}
                >
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </form>
        </Styled6>
      </Dialog>

      <Dialog
        open={activeDialog === 'unmute'}
        onClose={
          isUpdating
            ? undefined
            : () => {
                setActiveDialog(undefined);
              }
        }
      >
        <Box padding="small">
          <MenuItem disabled={true}>Alert Duration</MenuItem>

          {Object.entries(alertDurationOptions).map(([value, label]) => (
            <MenuItem
              key={value}
              disabled={isUpdating}
              onClick={() => {
                setActiveDialog(undefined);
                updateAlert({
                  alert_enabled: true,
                  alert_duration: value as AlertDuration,
                });
              }}
            >
              {label}
            </MenuItem>
          ))}
        </Box>
      </Dialog>
    </>
  );
}
